import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  ShieldCheck,
  Target,
  LineChart,
  Wallet,
  Link2,
  Search,
  FileText,
  Rocket,
  TrendingUp,
} from "lucide-react";
import Nav from "./Nav";
import Footer from "./Footer";
import MagneticButton from "./MagneticButton";
import { trackEvent } from "./GAListener";
import { formatLocalPrice, useCurrency } from "./currency";
import { CurrencySwitcher, PriceNote } from "./CurrencySwitcher";
import { Toaster } from "sonner";
import { AUDITS } from "./audits";
import { ebookByKey } from "./ebooks";
import { EbookCover } from "./EbookCard";
import { GumroadButton } from "./Gumroad";
import useSeo from "./useSeo";

const stepsFor = (a) => [
  { Icon: Link2, title: "Share Your Account Details", body: "Send read-only export info via the contact form — no risky logins shared." },
  { Icon: Search, title: "We Analyze Everything", body: "15+ checks across structure, targeting, creative, tracking and spend." },
  { Icon: FileText, title: "Get Your Audit Report", body: "A clear, prioritized report — findings, root causes, and what to fix first." },
  { Icon: Rocket, title: "Take Action", body: "Implement the roadmap yourself, or bring me in to execute it with you." },
  { Icon: TrendingUp, title: "Improve Performance & ROI", body: a.outcome },
];

const contactLinkFor = (tierName, from) => ({
  pathname: "/",
  hash: "contact",
  search: `?service=${encodeURIComponent(tierName)}&from=${encodeURIComponent(from)}`,
});

const DiySection = ({ a, book }) => (
  <section className="py-14 md:py-20 bg-paper" data-testid="audit-diy">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center border border-ink p-6 md:p-10">
        <div className="md:col-span-3">
          <EbookCover book={book} className="max-w-[220px]" />
        </div>
        <div className="md:col-span-9">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">Prefer to do it yourself?</span>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-4xl leading-[0.95] tracking-tightest">
            Get the playbook — {book.title}
          </h2>
          <p className="mt-4 text-base text-ink2 max-w-2xl leading-relaxed">{book.description}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 sm:items-center">
            <GumroadButton book={book} testId="audit-diy-buy" location={`audit_${a.key}_diy`} className="bg-ink text-paper hover:bg-signal px-7 py-4" />
            <Link to={`/playbook/${book.slug}`} data-testid="audit-diy-view" className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2 hover:text-signal">
              View Vol. {book.vol} →
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default function AuditPage({ platform = "linkedin" }) {
  const a = AUDITS[platform];
  const book = ebookByKey(platform);
  const STEPS = stepsFor(a);
  const { currency } = useCurrency();
  useSeo(`${a.platform} Account Audit | The Digital Marketer`, a.seoDescription);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [platform]);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid={`audit-page-${a.key}`}>
      <Nav />

      {/* Masthead */}
      <section className="pt-[120px] md:pt-[160px] pb-14 md:pb-20 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center justify-between border-t border-b border-line py-3 mb-10 md:mb-14 font-mono text-[11px] uppercase tracking-[0.22em] text-ink2"
          >
            <Link
              to="/"
              data-testid="audit-back-home"
              className="inline-flex items-center gap-2 hover:text-signal transition-colors"
            >
              <ArrowLeft size={12} /> Home
            </Link>
            <span className="text-right">{a.platform} Audit<span className="hidden sm:inline"> · The Digital Marketer</span></span>
            <span className="hidden md:inline">Free preview available</span>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="md:col-span-7"
            >
              <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-signal mb-6">
                ◆ Account Audit · <span style={{ color: a.accent }}>{a.platform}</span>
              </p>
              <h1
                data-testid="audit-heading"
                className="font-heading font-black text-[44px] sm:text-[64px] md:text-[88px] leading-[0.92] tracking-tightest"
              >
                Find wasted spend.
                <br />
                Fix what&apos;s <span className="text-signal">broken</span>.
              </h1>
              <p className="mt-8 text-lg md:text-xl text-ink2 leading-[1.45] max-w-xl">
                {a.intro}
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl">
                {[
                  { Icon: Target, label: "Data-driven insights" },
                  { Icon: LineChart, label: "Actionable recommendations" },
                  { Icon: Wallet, label: "Better ROI & lower CPL" },
                ].map(({ Icon, label }) => (
                  <div key={label} className="flex items-center gap-2">
                    <Icon size={16} className="text-signal flex-shrink-0" strokeWidth={1.5} />
                    <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-ink2 leading-tight">
                      {label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <MagneticButton strength={0.3}>
                  <Link
                    to={contactLinkFor(`${a.platform} Account Audit`, a.path)}
                    onClick={() => trackEvent("generate_lead", { method: "audit_cta", location: "audit_hero", platform: a.key })}
                    data-testid="audit-cta-primary"
                    className="inline-flex items-center gap-3 bg-ink text-paper hover:bg-signal px-8 py-5 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors"
                  >
                    Get Your {a.platform} Audit <ArrowUpRight size={16} />
                  </Link>
                </MagneticButton>
                <a
                  href="#pricing"
                  data-testid="audit-cta-secondary"
                  className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2 hover:text-signal transition-colors"
                >
                  See pricing ↓
                </a>
              </div>
            </motion.div>

            {/* Sample findings card — clearly labeled illustrative */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="md:col-span-5"
              data-testid="audit-sample-findings"
            >
              <div className="border border-ink bg-surface p-6 md:p-8" style={{ borderTop: `4px solid ${a.accent}` }}>
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
                    Sample Audit Findings
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-signal">
                    Illustrative
                  </span>
                </div>

                <div className="flex items-end gap-3 mb-2">
                  <span className="font-heading font-black text-6xl tracking-tightest leading-none">{a.sample.score}</span>
                  <span className="font-mono text-sm text-ink2 mb-1">/ 100</span>
                </div>
                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-signal mb-4">
                  Needs Improvement
                </div>
                <div className="h-2 bg-line mb-8">
                  <div className="h-2 bg-signal" style={{ width: `${a.sample.score}%` }} />
                </div>

                <div className="grid grid-cols-3 gap-3 mb-8 border-t border-line pt-6">
                  {a.sample.stats.map(([label, value]) => ({ label, value })).map((m) => (
                    <div key={m.label} className="min-w-0">
                      <div className="font-heading font-bold text-xs sm:text-sm md:text-sm lg:text-xl leading-none">{m.value}</div>
                      <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.16em] text-ink2">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-line pt-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink2 mb-3">
                    Top issues found
                  </div>
                  <ul className="space-y-2">
                    {a.issues.map((t) => (
                      <li key={t} className="flex items-start gap-2 text-[13px] text-ink2 leading-snug">
                        <span className="w-1.5 h-1.5 bg-signal mt-1.5 flex-shrink-0" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 md:py-24 bg-surface">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-4 mb-10 md:mb-14">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 01 — What's included</span>
            <span className="flex-1 h-px bg-line" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">{a.included.length} checks</span>
          </div>

          <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tightest mb-10 md:mb-14 max-w-4xl">
            Everything I check in your <span className="text-signal">{a.platform} audit</span>.
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-ink bg-paper" data-testid="audit-included-grid">
            {a.included.map(([Icon, title], i) => {
              const isLastCol = (i + 1) % 4 === 0;
              const isLastRow = i >= a.included.length - (a.included.length % 4 || 4);
              return (
                <div
                  key={title}
                  className={`p-6 md:p-8 bg-paper hover:bg-surface transition-colors border-line ${
                    !isLastCol ? "lg:border-r" : ""
                  } ${i % 2 === 0 ? "sm:border-r" : "sm:border-r-0"} ${!isLastRow ? "border-b" : ""}`}
                  data-testid={`included-${i}`}
                >
                  <Icon className="w-5 h-5 text-signal mb-4" strokeWidth={1.5} />
                  <div className="font-heading font-bold text-base md:text-lg leading-tight tracking-tight">
                    {title}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 md:py-24 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-4 mb-10 md:mb-14">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 02 — Choose your audit</span>
            <span className="flex-1 h-px bg-line" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">Simple. Transparent.</span>
          </div>

          <div className="mb-10 md:mb-14 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tightest max-w-4xl">
              Pricing shown in <span className="text-signal">your local currency</span>, billed in USD.
            </h2>
            <div className="flex flex-col gap-2 lg:items-end shrink-0">
              <CurrencySwitcher testId="audit-currency-switcher" />
              <PriceNote />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="audit-pricing-grid">
            {a.tiers.map((t) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6 }}
                data-testid={`pricing-card-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`flex flex-col p-8 border ${
                  t.featured
                    ? "border-ink bg-ink text-paper shadow-[8px_8px_0_0_#E63946]"
                    : "border-ink bg-paper text-ink"
                }`}
              >
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-4 ${
                    t.featured ? "text-signal" : "text-signal"
                  }`}
                >
                  {t.tag}
                </span>
                <h3 className="font-heading font-bold text-2xl leading-tight tracking-tight mb-3">{t.name}</h3>

                <div className="mt-2 mb-6">
                  <span className="font-heading font-black text-5xl tracking-tightest" data-testid={`pricing-value-${t.usd}`}>
                    {formatLocalPrice(t.usd, currency)}
                  </span>
                  {t.usd > 0 && (
                    <span className={`ml-2 font-mono text-[11px] uppercase tracking-[0.18em] ${t.featured ? "text-paper/60" : "text-ink2"}`}>
                      one-time
                    </span>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[14px] leading-snug">
                      <Check size={14} className="text-signal mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <MagneticButton strength={0.25} className="w-full">
                  <Link
                    to={contactLinkFor(t.name, a.path)}
                    onClick={() => trackEvent("generate_lead", { method: "audit_pricing_cta", platform: a.key, item_name: t.name, value: t.usd, currency: "USD" })}
                    data-testid={`pricing-cta-${t.name.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`w-full inline-flex items-center justify-center gap-3 px-6 py-4 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors ${
                      t.featured
                        ? "bg-signal text-paper hover:bg-paper hover:text-ink"
                        : "border border-ink hover:bg-ink hover:text-paper"
                    }`}
                  >
                    {t.cta} <ArrowUpRight size={16} />
                  </Link>
                </MagneticButton>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-6 font-mono text-[11px] uppercase tracking-[0.2em] text-ink2">
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-signal" /> 100% confidential</span>
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-signal" /> Secure data</span>
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-signal" /> No long-term commitment</span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-ink text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-4 mb-10 md:mb-14">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 03 — How it works</span>
            <span className="flex-1 h-px bg-paper/20" />
          </div>

          <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tightest mb-10 md:mb-14 max-w-4xl">
            Five steps to a <span className="text-signal">healthier</span> account.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-6" data-testid="audit-steps">
            {STEPS.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`audit-step-${i}`}
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full border border-signal text-signal flex items-center justify-center font-mono text-xs">
                    {i + 1}
                  </div>
                  <Icon size={18} className="text-paper/70" strokeWidth={1.5} />
                </div>
                <div className="font-heading font-bold text-lg leading-tight tracking-tight mb-2">{title}</div>
                <p className="text-[13px] text-paper/60 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-surface">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center border border-ink bg-paper p-8 md:p-12">
            <div className="md:col-span-8">
              <h2 className="font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tightest">
                Stop guessing. <span className="text-signal">Start growing.</span>
              </h2>
              <p className="mt-4 text-base md:text-lg text-ink2 max-w-xl">
                Get your {a.platform} audit today and uncover the opportunities you&apos;re leaving
                on the table.
              </p>
              <ul className="mt-6 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink2">
                {a.credentials.map((c) => (
                  <li key={c} className="flex items-center gap-2"><Check size={12} className="text-signal" /> {c}</li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-4 md:justify-self-end">
              <MagneticButton strength={0.3} className="w-full">
                <Link
                  to={contactLinkFor(`${a.platform} Account Audit`, a.path)}
                  onClick={() => trackEvent("generate_lead", { method: "audit_cta", location: "audit_final_band", platform: a.key })}
                  data-testid="audit-cta-final"
                  className="w-full inline-flex items-center justify-center gap-3 bg-ink text-paper hover:bg-signal px-8 py-5 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors"
                >
                  Get Your Audit <ArrowUpRight size={16} />
                </Link>
              </MagneticButton>
            </div>
          </div>
        </div>
      </section>

      {book && <DiySection a={a} book={book} />}

      <Footer />
      <Toaster
        position="bottom-right"
        offset={104}
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#FDFDFD",
            border: "1px solid #2A2A2A",
            borderRadius: 0,
            fontFamily: "Satoshi, sans-serif",
          },
        }}
      />
    </div>
  );
}
